import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { PageHeader } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { useSnackBar } from "@/utils/useSnackbar";

import { StaffNewTicketModal } from "./components/StaffNewTicketModal";
import { StaffTicketFilterBar } from "./components/StaffTicketFilterBar";
import { StaffTicketsTable } from "./components/StaffTicketsTable";
import { TicketTriageStrip } from "./components/TicketTriageStrip";
import { useStaffTicketQueue } from "./hooks/useStaffTicketQueue";
import { STAFF_TICKET_TABS, StaffTicketTab } from "./utils/staffTicketTabs";

/**
 * The staff queue, /app/tickets (PREC-3939, spec §7 "Staff"). Reached only through the
 * GLOBAL_ADMIN route; no feature flag anywhere on this page. The triage strip sits under the tab
 * strip (PageHeader owns title bar + tabs) and above the filter row.
 */
export const Tickets: FunctionComponent = () => {
	const { t } = useTranslation();
	const controller = useStaffTicketQueue();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const [isNewTicketOpen, setIsNewTicketOpen] = useState(false);

	const tabs = STAFF_TICKET_TABS.map((tab) => ({
		value: tab,
		label: (
			<span className="flex items-center gap-2">
				{t(`support.staff.tabs.${tab}`)}
				<span className="rounded-full bg-status-info-light px-1.5 py-0.5 text-[11px] font-semibold text-text-meta">
					{controller.tabCounts[tab] ?? "–"}
				</span>
			</span>
		),
	}));

	const handleCreated = (ticketId: number, name: string, failedAttachment?: string) =>
		handleOpenSnackbar(
			failedAttachment
				? t("support.staff.modal.createdWithFailedUpload", {
						id: ticketId,
						name,
						file: failedAttachment,
					})
				: t("support.staff.modal.created", { id: ticketId, name })
		);

	return (
		<div className="flex h-full w-full flex-col">
			<PageHeader
				activeTab={controller.tab}
				actions={
					<Button
						size={ButtonSize.XS}
						title={t("support.staff.newTicketForCustomer")}
						onClick={() => setIsNewTicketOpen(true)}
					/>
				}
				tabs={tabs}
				title={t("support.staff.title")}
				onTabChange={(value) => controller.setTab(value as StaffTicketTab)}
			/>

			<TicketTriageStrip stats={controller.stats} />
			<StaffTicketFilterBar controller={controller} />
			<StaffTicketsTable controller={controller} />

			{isNewTicketOpen && (
				<StaffNewTicketModal onClose={() => setIsNewTicketOpen(false)} onCreated={handleCreated} />
			)}
			{renderMessageSnackbar()}
		</div>
	);
};
